/**
 * salesBrain — cómo se lee en pantalla el análisis comercial de un lead.
 *
 * Puro y sin React: entra el `analysis` que devuelve
 * GET /leads/{id}/conversation-intelligence y salen las cadenas que pinta la
 * tarjeta. Aquí no se decide NADA: la puntuación, los huecos y el objetivo
 * los calcula el backend (`lead_scoring.py`), que es el único sitio donde
 * viven los puntos. Este fichero solo traduce a español y a colores.
 *
 * Por eso las etiquetas de los motivos, los huecos y el objetivo NO se
 * reescriben aquí: viajan con el análisis. Un snapshot guardado hace seis
 * meses tiene que poder explicarse a sí mismo aunque el baremo haya
 * cambiado desde entonces, y eso solo funciona si la etiqueta se guardó
 * junto a los puntos.
 *
 * La regla de honestidad del proyecto manda sobre todo lo demás: un dato
 * que no sabemos se enseña como desconocido, nunca se rellena con algo
 * plausible. Y un dato INFERIDO se enseña marcado como inferido — el
 * comercial tiene que poder distinguir "lo dijo" de "eso creemos".
 */

/** Bandas de la puntuación. `unknown` no es frío: es que no sabemos nada. */
export const BAND_META = {
  hot: {
    label: "Lead caliente",
    cls: "bg-red-50 border-red-200 text-red-700 dark:bg-red-950/40 dark:border-red-900 dark:text-red-300",
  },
  warm: {
    label: "Lead templado",
    cls: "bg-amber-50 border-amber-200 text-amber-800 dark:bg-amber-950/40 dark:border-amber-900 dark:text-amber-300",
  },
  cold: {
    label: "Lead frío",
    cls: "bg-sky-50 border-sky-200 text-sky-700 dark:bg-sky-950/40 dark:border-sky-900 dark:text-sky-300",
  },
  unknown: {
    label: "Sin cualificar",
    cls: "bg-stone-50 border-stone-200 text-stone-500 dark:bg-stone-800/60 dark:border-stone-700 dark:text-stone-400",
  },
};

export function bandMeta(band) {
  return BAND_META[band] || BAND_META.unknown;
}

/**
 * Qué se puede hacer con el borrador.
 *
 * En esta versión NINGUNA de las tres envía nada: `DRAFT_READY` significa
 * "un agente autónomo podría contestar esto sin riesgo", no "esto ya se ha
 * mandado". La distinción importa porque es la que decidirá, en la
 * siguiente fase, qué se puede automatizar y qué no.
 */
export const DECISION_META = {
  DRAFT_READY: {
    label: "Borrador listo",
    hint: "Se sostiene con lo que sabemos. Aun así lo envías tú.",
    cls: "bg-emerald-50 border-emerald-200 text-emerald-700 dark:bg-emerald-950/40 dark:border-emerald-900 dark:text-emerald-300",
  },
  HUMAN_REVIEW: {
    label: "Revisar antes de enviar",
    hint: "Toca algo que la IA no puede afirmar por su cuenta.",
    cls: "bg-amber-50 border-amber-200 text-amber-800 dark:bg-amber-950/40 dark:border-amber-900 dark:text-amber-300",
  },
  NO_REPLY: {
    label: "No responder",
    hint: "No procede escribir a este lead.",
    cls: "bg-stone-100 border-stone-300 text-stone-600 dark:bg-stone-800 dark:border-stone-600 dark:text-stone-300",
  },
};

export function decisionMeta(decision) {
  return DECISION_META[decision] || DECISION_META.HUMAN_REVIEW;
}

/** Por qué hay que revisarlo. Códigos del backend → español. */
export const REVIEW_REASON_TEXT = {
  commercial_negotiation: "Negociación o descuentos",
  financial_claim: "Cifras financieras o rentabilidad",
  legal_claim: "Asuntos legales o fiscales",
  missing_information: "Falta información respaldada",
  sensitive_commitment: "Compromiso sensible (reserva, pago, cita)",
  opted_out: "El lead pidió no recibir mensajes",
  no_draft: "La IA no ha propuesto ningún texto",
  low_confidence: "La IA no está segura del borrador",
};

export function reviewReasonText(code) {
  return REVIEW_REASON_TEXT[code] || code || null;
}

const INTENT_TEXT = {
  buy: "Compra",
  invest: "Inversión",
  explore: "Explorando",
};

const TIMELINE_TEXT = {
  lt_3m: "menos de 3 meses",
  m3_6: "3-6 meses",
  m6_12: "6-12 meses",
  gt_12m: "más de un año",
};

/** ¿Es un dato que el lead dijo, o algo que estamos deduciendo? */
export function isExplicit(field) {
  return (field || {}).basis === "explicit";
}

function usable(field) {
  const f = field || {};
  if (f.basis !== "explicit" && f.basis !== "inferred") return false;
  return Boolean(f.value || f.bucket || f.raw_expression
    || f.min != null || f.max != null);
}

/** El presupuesto tal cual lo expresó el lead — sin convertir divisas. */
export function budgetText(budget) {
  const b = budget || {};
  if (b.raw_expression) return b.raw_expression;
  const cur = b.currency || "";
  const fmt = (n) => Number(n).toLocaleString("es-ES");
  if (b.min != null && b.max != null) return `${fmt(b.min)}-${fmt(b.max)} ${cur}`.trim();
  if (b.min != null) return `desde ${fmt(b.min)} ${cur}`.trim();
  if (b.max != null) return `hasta ${fmt(b.max)} ${cur}`.trim();
  return null;
}

/**
 * La línea de resumen: "Inversión · 90-110k · menos de 3 meses".
 *
 * Solo entra lo que sabemos. Un hueco no se rellena con un guion dentro de
 * la frase: se cae de la frase y aparece abajo, en "falta conocer", que es
 * donde sirve para algo.
 */
export function qualificationLine(qualification) {
  const q = qualification || {};
  const parts = [];
  if (usable(q.intent) && INTENT_TEXT[q.intent.value]) {
    parts.push(INTENT_TEXT[q.intent.value]);
  }
  const budget = usable(q.budget) ? budgetText(q.budget) : null;
  if (budget) parts.push(budget);
  if (usable(q.purchase_timeline)) {
    const tl = q.purchase_timeline;
    parts.push(TIMELINE_TEXT[tl.bucket] || tl.value);
  }
  return parts.filter(Boolean).join(" · ") || null;
}

/** Los campos de cualificación que SÍ conocemos, para la vista ampliada. */
export function knownFields(qualification) {
  const q = qualification || {};
  const rows = [];
  const push = (key, label, value, field) => {
    if (!value) return;
    rows.push({ key, label, value, explicit: isExplicit(field) });
  };
  push("intent", "Intención", INTENT_TEXT[(q.intent || {}).value], q.intent);
  push("budget", "Presupuesto", usable(q.budget) ? budgetText(q.budget) : null, q.budget);
  const tl = q.purchase_timeline || {};
  push("purchase_timeline", "Cuándo compra",
    usable(tl) ? (TIMELINE_TEXT[tl.bucket] || tl.value) : null, tl);
  [
    ["preferred_product", "Producto"],
    ["investment_goal", "Objetivo de inversión"],
    ["financing", "Financiación"],
    ["location_preference", "Ubicación"],
    ["decision_makers", "Quién decide"],
    ["call_availability", "Llamada"],
  ].forEach(([key, label]) => {
    const f = q[key] || {};
    push(key, label, usable(f) ? f.value : null, f);
  });
  return rows;
}

/**
 * El hueco que toca cubrir AHORA. Uno solo.
 *
 * El backend ya devuelve la lista ordenada por valor comercial; la pantalla
 * enseña el primero. Enseñar los seis convertiría la tarjeta en un
 * cuestionario, y quien lo lee acabaría preguntándolos todos de golpe — que
 * es exactamente lo que esta función existe para evitar.
 */
export function nextGap(missingInformation) {
  const list = Array.isArray(missingInformation) ? missingInformation : [];
  return list.length ? list[0] : null;
}

/** El número, o null si nunca se ha analizado. */
export function scoreValue(analysis) {
  const s = (analysis || {}).score;
  return s && Number.isFinite(Number(s.value)) ? Number(s.value) : null;
}
